import {
  ContactCandidates,
  ContactEnrichments,
} from "../models/peopleCandidate";
import { HubSpotMatches } from "../models/hubspotMatch";
import { LeadValue, TenantCommercial } from "./postLeadSearch";
import axiosConfig from "./axiosConfig";

// The operational InRange site the operator anchored the search on when this lead
// was promoted, snapshotted at promote time (so a later search elsewhere can't change
// it). Drives the "near <site>" chip on the leads panes and the cold-email proximity
// hook. null when the lead wasn't promoted from a "search near a site".
export interface ProximityContext {
  anchorSiteId: string;
  anchorSiteName: string | null;
  distanceMetres: number | null;
  operationalAtCapture: boolean;
  capturedBy: string | null;
  capturedAt: string | null;
}

// One lead's position in the contacts pipeline: find contacts -> rank them -> pull
// contact details -> draft emails, or the ending it reached. The BE owns both the
// stage machine and the wording (a count of drafts, or a named outcome), so the FE
// renders `label` rather than re-deriving copy that would then disagree with the panel.
export interface LeadPipelineStatus {
  // Machine key: a stage ("finding" | "ranking" | "pulling" | "drafting"), a terminal
  // ("drafted" | "quota_spent" | "not_run" | "truncated" | "no_email" |
  // "no_top_targets" | "no_contacts"), or "no_company" when no company is chosen yet.
  stage: string;
  // What the column shows. Empty for "no_company" - the row is waiting on the
  // operator, not on us, so it renders nothing rather than a status.
  label: string;
  // Progress through the stage, present only where there is a real denominator
  // (pulling details, drafting emails). Discovery's only x/y is Apollo pages and
  // ranking is one model call, so neither carries one.
  done: number | null;
  total: number | null;
  // An operation is actually in flight for this lead right now. False on a stage
  // means the work stopped there - the label already says so ("1 of 3 emails
  // drafted"), and this is what drives the spinner.
  running: boolean;
}

export interface Lead {
  id: string;
  source: string;
  sourceRef: string;
  sourcePayload: Record<string, unknown> | null;
  // Property info
  address: string | null;
  postcode: string | null;
  postcodeOutward: string | null;
  latitude: number | null;
  longitude: number | null;
  floorAreaSqm: number | null;
  declaredNumBuildings: number | null;
  occupierName: string | null;
  // Per-source enrichment (always populated for CSV-imported leads;
  // copied from the VOA row at promotion time for VOA-promoted leads)
  polygonData: Record<string, unknown> | null;
  hmlrData: Record<string, unknown> | null;
  tenantData: Record<string, unknown> | null;
  // Agentic landlord-research candidate pool ({matches, researchedAt, from}),
  // copied from the VOA row at promotion. See AgenticMatch for the item shape.
  agenticData: Record<string, unknown> | null;
  // The operationKey of a landlord-research run (single or bulk) that is LIVE for this
  // row right now, else null. Server-derived per response, so any surface rendering the
  // row shows the true state: the launching page's in-memory handle is lost by
  // navigating away, searching another area, or opening a second tab.
  researchOperationKey: string | null;
  // Operator decisions (atomic via PATCH /leads/<id>/decisions)
  polygonDecision: Record<string, unknown> | null;
  landlordDecision: Record<string, unknown> | null;
  tenantDecision: Record<string, unknown> | null;
  // Step-5 people-discovery result keyed by role ("landlord"/"tenant"); null
  // until a "Find people" run has happened. Rendered on the lead panel.
  contactCandidates: ContactCandidates | null;
  // Step-7 paid contact enrichments keyed by role; null until a "Pull contact
  // info" run has happened. Separate from contactCandidates so a re-discovery
  // can't wipe paid reveals. Rendered in the per-role Contact details tile.
  contactEnrichments: ContactEnrichments | null;
  // What HubSpot holds for this lead's landlord / tenant company, keyed by role.
  hubspotMatches: HubSpotMatches | null;
  // Size + UK footprint of this lead's tenant, and the value band the inbox
  // sorts on. Same shapes and same BE builders as the search results, so the
  // two surfaces cannot disagree. Null until the commercial pass has run for
  // this lead's tenant.
  tenantCommercial: TenantCommercial | null;
  leadValue: LeadValue | null;
  // The operational site anchored on when this lead was promoted from a "search near
  // a site"; null otherwise. Rendered as a "near <site>" chip on the leads panes.
  proximityContext: ProximityContext | null;
  ownerOccupier: boolean;
  // Operator-flagged: this building already carries a solar PV installation.
  // False means "not flagged", never "no solar" - nothing infers an absence from
  // it. The outreach drafter uses the EFFECTIVE value (this OR the linked site's
  // own declared existing capacity, see `siteExistingPvKwpByLeadId`), not this alone.
  hasPv: boolean;
  tags: string[];
  // Dedupe
  duplicateOfLeadID: string | null;
  matchedExistingSiteID: string | null;
  createdAt: string;
  updatedAt: string;
  // Server-computed: minimum of polygon + landlord + tenant per-element
  // confidences (0-100). null when any required decision is missing -
  // treat as "incomplete" for site-creation purposes, not "low".
  leadConfidence: number | null;
}

export interface LeadListResponse {
  leads: Lead[];
  // Backlinks for each lead to any sites whose `originatedFromLeadId`
  // matches it. Lets the leads table render a [Site] button on rows
  // that already have a site created from them. Empty entries are
  // omitted from the map.
  siteIdsByLeadId: Record<string, string[]>;
  // Total building area (m²) of each lead's *linked* site (the site it
  // created, else its `matchedExistingSiteID`), so the table can show the
  // site's real footprint instead of the lead's VOA/OSM candidate area.
  // Leads with no linked site (or an unresolvable one) are omitted.
  siteBuildingAreaByLeadId: Record<string, number>;
  // Existing solar (kWp) declared on each lead's *linked* site's Demand tab, present
  // only when non-zero. The derived half of `Lead.hasPv`: a lead here has existing
  // solar whatever its own flag says, so the panel renders the toggle on and
  // read-only. The outreach drafter derives the same signal from the same site
  // field, so the two surfaces agree by construction.
  siteExistingPvKwpByLeadId: Record<string, number>;
  // The InRange install capacity (kWp) of each lead's *linked* site - its
  // `totalInstalledCapacity`, i.e. the system we would install, which is a
  // different number from the existing array above. Leads with no linked site,
  // or whose site has no capacity yet, are omitted. The outreach drafter quotes
  // this same figure as "Indicative InRange install size".
  siteInstallCapacityKwpByLeadId: Record<string, number>;
  // Apollo contact-enrichment credits spent on each lead, keyed by role
  // ("landlord" / "tenant") and summed over every pull the lead has had - the
  // ledger is append-only, so a re-pull adds a row rather than replacing one.
  // Leads and roles that have spent nothing are omitted. Rides the list rather
  // than the pipeline-status poll because credits only change when a run ends,
  // which is exactly when this list is refetched.
  apolloCreditsByLeadId: Record<string, Record<string, number>>;
}

const getLeads = async (authToken: string): Promise<LeadListResponse> => {
  const { data } = await axiosConfig.get(`/leads`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getLeads;

export interface LeadPipelineStatusResponse {
  pipelineStatusByLeadId: Record<string, LeadPipelineStatus>;
}

// Just the Status column's values. Polled every few seconds while anything is in
// flight, which is why it is NOT the full /leads read: that is every lead plus five
// batched maps plus an operations scan per op type, and running it on a poll cadence
// per open tab is what drains the shared connection pool.
export const getLeadsPipelineStatus = async (
  authToken: string
): Promise<LeadPipelineStatusResponse> => {
  const { data } = await axiosConfig.get(`/leads/pipeline-status`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};
